import { RouterView, type RouteRecordRaw } from "vue-router";
import { commerceRoutes } from "@/router/commerce_routes";
import { insightsRoutes } from "@/router/insights_routes";
import { marketingRoutes } from "@/router/marketing_routes";
import { dashboardRoute } from "@/router/reporting_routes";
import { salesRoutes } from "@/router/sales_routes";
import { settingsRoute } from "@/router/setting_routes";
import { toolsRoutes } from "@/router/tools_routes";
import { websiteRoutes } from "@/router/website_routes";
import { Tier } from "@/services/organization";

export const homeRoute: RouteRecordRaw = {
  path: "/home",
  name: "home",
  meta: {
    activityArea: "home",
    title: "Home",
    text: "Home",
    icon: "house-lucide",
    description: "Your customizable homepage",
    requiredResources: [],
    requiresAuth: true,
    isDirect: true,
    contentWidth: "full-bleed",
    breadcrumb: false,
  },
  component: () => import("@/views/Home/HomeView.vue"),
};

export const feedRoute: RouteRecordRaw = {
  path: "/feed",
  name: "feed_shell",
  redirect: { name: "feed" },
  meta: {
    activityArea: "content",
    title: "Feed",
    text: "Feed",
    icon: "newspaper-lucide",
    description: "Your activity feed with updates and insights",
    requiredResources: [],
    requiresAuth: true,
    isDirect: true,
    breadcrumb: { label: "Home", to: { name: "home" } },
  },
  component: RouterView,
  children: [
    {
      path: "",
      name: "feed",
      meta: {
        title: "Feed",
        breadcrumb: false,
      },
      redirect: (to) =>
        to.query.feedItemId
          ? {
              name: "feed-post",
              params: { id: String(to.query.feedItemId) },
              query: to.query,
            }
          : { name: "home", query: to.query },
    },
    {
      path: "new",
      name: "feed-new-post",
      meta: {
        title: "New Feed Post",
        breadcrumb: { label: "New post" },
      },
      redirect: (to) => ({
        name: "home",
        query: { ...to.query, newPost: "true" },
      }),
    },
    {
      path: ":id",
      name: "feed-post",
      meta: {
        title: "Feed Post",
        breadcrumb: {
          label: "Feed post",
          dynamic: true,
          fallback: "Feed post",
        },
      },
      component: () => import("@/views/Feed/FeedPostView.vue"),
    },
  ],
};

export const homeRoutes: RouteRecordRaw[] = [homeRoute];
export const feedRoutes: RouteRecordRaw[] = [feedRoute];

export const competitorMonitorRoute: RouteRecordRaw = {
  path: "/competitor-monitor",
  name: "competitor-monitor",
  redirect: { name: "competitor-overview" },
  meta: {
    activityArea: "competitors",
    title: "Competitor Monitoring",
    text: "Competitors",
    icon: "map",
    requiredTier: Tier.LARGE,
    gtmEventCategory: "Reporting - Competitor monitor",
    description: "Spy on what your competitors are doing on social media",
    updated: "2023-06-14",
    requiresAuth: true,
    contentWidth: "wide",
    breadcrumb: {
      label: "Competitor Monitoring",
      to: { name: "competitor-overview" },
    },
  },
  component: () =>
    import("@/views/CompetitorMonitor/CompetitorMonitorBase.vue"),
  children: [
    {
      path: "overview",
      name: "competitor-overview",
      meta: {
        title: "Competitor overview",
        text: "Overview",
        isDirect: true,
        breadcrumb: false,
      },
      component: () =>
        import("@/views/CompetitorMonitor/CompetitorOverviewTab.vue"),
    },
    {
      path: "posts",
      name: "competitor_posts_shell",
      redirect: { name: "competitor-posts" },
      meta: {
        title: "Competitor posts",
        isDirect: true,
        breadcrumb: {
          label: "Posts",
          to: { name: "competitor-posts" },
        },
      },
      component: RouterView,
      children: [
        {
          path: "",
          name: "competitor-posts",
          meta: { title: "Competitor posts", breadcrumb: false },
          component: () =>
            import("@/views/CompetitorMonitor/CompetitorPostsTab.vue"),
        },
        {
          path: ":id",
          name: "competitor-post",
          meta: {
            title: "Competitor Post",
            breadcrumb: {
              label: "Competitor post",
              dynamic: true,
              fallback: "Competitor post",
            },
          },
          component: () =>
            import("@/views/CompetitorMonitor/CompetitorSinglePostView.vue"),
        },
      ],
    },
    {
      path: "share-of-search",
      name: "share-of-search",
      meta: { title: "Share of search", isDirect: true, breadcrumb: false },
      component: () => import("@/views/CompetitorMonitor/ShareOfSearchTab.vue"),
    },
    {
      path: "ai-visibility",
      name: "ai_visibility",
      redirect: { name: "ai_visibility_overview" },
      meta: {
        activityArea: "ai_visibility",
        title: "AI Visibility",
        text: "AI Visibility",
        isDirect: true,
        requiredFeatureFlag: "aiVisibility",
        // In-component, not `true`: CompetitorMonitorBase renders the paywall
        // inside its own shell, keeping the tab bar and action bar around the
        // pitch. PageFrame's route-level paywall would replace the whole
        // shell, leaving a locked org no way back to Overview / Posts /
        // Share of search.
        paywall: "in-component",
        breadcrumb: {
          label: "AI Visibility",
          to: { name: "ai_visibility_overview" },
        },
        beta: true,
      },
      component: RouterView,
      children: [
        {
          path: "",
          name: "ai_visibility_overview",
          meta: { title: "AI Visibility", breadcrumb: false },
          component: () =>
            import("@/views/AIVisibility/AIVisibilityOverviewTab.vue"),
        },
        {
          path: "sources",
          name: "ai_visibility_sources",
          meta: {
            title: "AI Visibility - Sources",
            excludeFromNavbar: true,
            breadcrumb: {
              label: "Sources",
              fallback: "AI Visibility sources",
            },
          },
          component: () =>
            import("@/views/AIVisibility/AIVisibilitySourcesTab.vue"),
        },
        {
          path: "recommendations",
          name: "ai_visibility_recommendations",
          meta: {
            title: "AI Visibility - Recommendations",
            excludeFromNavbar: true,
            // The parent's paywall would make the router skip this check, so
            // opt out of it: this flag is a rollout switch, not something we
            // sell, and a locked org should get a 404 rather than a pitch.
            requiredFeatureFlag: "aiVisibilityRecommendations",
            paywall: false,
            breadcrumb: {
              label: "Recommendations",
              fallback: "AI Visibility recommendations",
            },
          },
          component: () =>
            import("@/views/AIVisibility/AIVisibilityRecommendationsTab.vue"),
        },
        {
          path: "sentiment",
          name: "ai_visibility_sentiment",
          meta: {
            title: "AI Visibility - Sentiment",
            excludeFromNavbar: true,
            breadcrumb: {
              label: "Sentiment",
              fallback: "AI Visibility sentiment",
            },
          },
          component: () =>
            import("@/views/AIVisibility/AIVisibilitySentimentTab.vue"),
        },
        {
          path: "prompts",
          name: "ai_visibility_prompts",
          meta: {
            title: "AI Visibility - Prompts",
            excludeFromNavbar: true,
            breadcrumb: {
              label: "Prompts",
              fallback: "AI Visibility prompts",
            },
          },
          component: () =>
            import("@/views/AIVisibility/AIVisibilityPromptsTab.vue"),
        },
      ],
    },
    {
      path: "competitors",
      name: "competitors",
      meta: {
        title: "Competitor settings",
        text: "Settings",
        isDirect: true,
        breadcrumb: false,
      },
      component: () => import("@/views/CompetitorMonitor/CompetitorsTab.vue"),
    },
  ],
};

export const analyticsRoute: RouteRecordRaw = {
  path: "/marketing",
  name: "analytics",
  redirect: { name: "goals" },
  meta: {
    title: "Analytics",
    icon: "flask-conical-lucide",
    description: "Analyze your campaigns, ads, and performance",
    requiresAuth: true,
    contentWidth: "wide",
    breadcrumb: { label: "Analytics", to: { name: "analytics" } },
  },
  children: [...marketingRoutes, ...websiteRoutes, ...commerceRoutes],
};

export const budgetRoute: RouteRecordRaw = {
  path: "/sales",
  name: "budget",
  redirect: { name: "spending-report" },
  meta: {
    activityArea: "budgets",
    title: "Budget",
    text: "Budget",
    icon: "wallet-lucide",
    description: "Analyze your spending and plan your budgets",
    requiresAuth: true,
    contentWidth: "wide",
    breadcrumb: { label: "Budget", to: { name: "budget" } },
  },
  children: salesRoutes,
};

// Not "/tools": the Vite dev proxy forwards that prefix to Django.
export const toolsRoute: RouteRecordRaw = {
  path: "/toolbox",
  name: "tools",
  redirect: { name: "create-content" },
  meta: {
    activityArea: "tools",
    title: "Tools",
    text: "Tools",
    icon: "wrench-lucide",
    description: "Utilities that help you create and track content",
    requiresAuth: true,
    breadcrumb: { label: "Tools", to: { name: "tools" } },
  },
  children: toolsRoutes,
};

export const navbarRoutes: RouteRecordRaw[] = [
  homeRoute,
  dashboardRoute,
  analyticsRoute,
  competitorMonitorRoute,
  ...insightsRoutes,
  budgetRoute,
  toolsRoute,
  settingsRoute,
];
